import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { TaskItemView } from '../../api/generated/models/TaskItemView';
import { getTaskStatusClassName } from '../../utils/taskHelpers';
import AssigneeAvatars from './AssigneeAvatars';

interface TaskCardProps {
  task: TaskItemView;
  onClick: (taskId: string) => void;
  draggable?: boolean;
  isDragging?: boolean;
  onDragStart?: (taskId: string) => void;
  onDragEnd?: () => void;
}

const TaskCard: React.FC<TaskCardProps> = ({
  task,
  onClick,
  draggable = false,
  isDragging = false,
  onDragStart,
  onDragEnd,
}) => {
  const { t } = useTranslation();
  const taskId = task.id ?? '';
  const statusClassName = getTaskStatusClassName(task.status);
  const didDragRef = useRef(false);

  const handleClick = () => {
    if (didDragRef.current || !taskId) {
      return;
    }
    onClick(taskId);
  };

  const handleDragStart = (event: React.DragEvent<HTMLDivElement>) => {
    if (!taskId) {
      return;
    }

    didDragRef.current = true;
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', taskId);
    onDragStart?.(taskId);
  };

  const handleDragEnd = () => {
    window.setTimeout(() => {
      didDragRef.current = false;
    }, 0);
    onDragEnd?.();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleClick();
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      draggable={draggable}
      className={`tasks-card${isDragging ? ' tasks-card--dragging' : ''}${draggable ? ' tasks-card--draggable' : ''}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onDragStart={draggable ? handleDragStart : undefined}
      onDragEnd={draggable ? handleDragEnd : undefined}
    >
      <h3 className="tasks-card-title">{task.title || t('tasks_untitled')}</h3>
      <p className={`tasks-card-status ${statusClassName}`}>
        {t(`tasks_status_${task.status ?? 'open'}`)}
      </p>
      <div className="tasks-card-footer">
        <AssigneeAvatars
          assignees={(task.assignees ?? [])
            .filter((row): row is typeof row & { id: string } => Boolean(row.id))
            .map((row) => ({
              id: row.id,
              name: row.name,
              initials: row.initials,
            }))}
        />
        <span className={`tasks-card-tag tasks-card-tag--${task.source ?? TaskItemView.source.CUSTOM}`}>
          {t(task.source === TaskItemView.source.CUSTOM
            ? 'tasks_source_custom'
            : 'tasks_source_listing_inspector')}
        </span>
      </div>
    </div>
  );
};

export default TaskCard;
